import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../lib/utils";

// Set up axios interceptor to always send token and device token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const deviceToken = localStorage.getItem("device_token");
  if (deviceToken) {
    config.headers["X-Device-Token"] = deviceToken;
  }
  config.withCredentials = true;
  return config;
});

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState("unknown");

  const checkAuth = useCallback(async () => {
    try {
      // Ensure device token exists
      if (!localStorage.getItem("device_token")) {
        localStorage.setItem("device_token", "dev_" + crypto.randomUUID().replace(/-/g, ""));
      }
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setDeviceStatus(response.data.device_status || "approved");
      setError(null);
    } catch (err) {
      setUser(null);
      setDeviceStatus("unknown");
      localStorage.removeItem("session_token");
      if (err.response?.status !== 401) {
        console.error("Auth check failed:", err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processSessionId = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId }
      );
      if (response.data.token) {
        localStorage.setItem("session_token", response.data.token);
      }
      setUser(response.data.user);
      return response.data.user;
    } catch (err) {
      console.error("Session processing failed:", err);
      setError("Authentication failed. Please try again.");
      throw err;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      localStorage.removeItem("session_token");
      setUser(null);
    }
  };

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    logout,
    processSessionId,
    checkAuth,
    deviceStatus,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === "super_admin",
    isAdmin: user?.role === "admin" || user?.role === "super_admin",
    isAdminOnly: user?.role === "admin",
    isMerchant: user?.role === "merchant",
    isManager: ["prepress_manager", "production_manager", "logistics_manager", "packaging_manager"].includes(user?.role),
    isHoD: ["merchandizing_hod", "prepress_hod", "production_hod", "logistics_hod", "packaging_hod"].includes(user?.role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
